import { memo, useEffect, useId, useMemo, useState, useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import type { Question } from '../../config/types';
import { useFormData } from '../../hooks/useFormData';
import {
  chainValidations,
  objectToString,
  stringToObject,
  groupValidation,
} from '../../utils/form-utils';
import { dateIsEmpty } from './DateInputField/utils';
import Details from './Details';
import ErrorMessage from './ErrorMessage';

export const getColumns = () => ['Yes', 'No', 'Partial', 'NA'];
export const getRows = () => [
  'staffEngagementExercisesBehaviouralPsychologist',
  'onlineTests',
  'grs',
  'applicantTrackingSystems',
  'preEmploymentChecks',
  'executiveSearchTeams',
  'complexCampaignManagementFees',
  'advertisingServices',
  'other',
];
export const getKeys = () => getRows().flatMap((r) => getColumns().map((c) => `${r}${c}`));

type RowMap = Record<string, Record<string, string>>;

type Props = {
  question: Question;
  disabled?: boolean;
  anchorId?: string;
  detailsHeading?: string;
  detailsContent?: string;
};

export function CoreCostExternalInput({
  question,
  disabled,
  anchorId,
  detailsHeading,
  detailsContent,
}: Props) {
  const { register, getFieldState, getValues, setValue } = useFormContext();
  const { error } = getFieldState(question.name);
  const { data } = useFormData();

  const defaults = useMemo(() => {
    const base: RowMap = {};
    getRows().forEach((row) => {
      base[row] = {};
      getColumns().forEach((col) => {
        base[row][col] = '0';
      });
    });
    return base;
  }, []);

  useEffect(() => {
    if (getValues(question.name) === undefined || getValues(question.name) === '') {
      const flatDefaults = Object.fromEntries(
        getRows().flatMap((row) => getColumns().map((col) => [`${row}${col}`, '0'])),
      );
      setValue(question.name, objectToString(flatDefaults), {
        shouldDirty: false,
        shouldValidate: false,
      });
    }
  }, [getValues, question.name, setValue]);

  const aId = useId();
  const makeId = (s: string) => `${anchorId ?? aId}-${s}`;

  const buildRowMap = useCallback(
    (raw: Record<string, string>): RowMap => {
      const map: RowMap = {};
      getRows().forEach((row) => {
        map[row] = {};
        getColumns().forEach((col) => {
          map[row][col] = raw[`${row}${col}`] ?? defaults[row][col];
        });
      });
      return map;
    },
    [defaults],
  );

  const initialValues = useMemo(() => {
    const parsed = stringToObject(getValues(question.name));
    return buildRowMap(parsed);
  }, [getValues, question.name, buildRowMap]);
  const initialOtherSpecify = useMemo(() => {
    const parsed = stringToObject(getValues(question.name));
    const value = parsed.otherSpecify;
    return typeof value === 'string' ? value : '';
  }, [getValues, question.name]);
  const [values, setValues] = useState<RowMap>(initialValues);
  const [otherSpecify, setOtherSpecify] = useState<string>(initialOtherSpecify);

  const { onChange } = register(question.name, {
    validate: (value: string) =>
      chainValidations([
        groupValidation({
          value: stringToObject(value),
          answers: data?.answers,
          question,
          sumColumns: [],
        }),
      ]),
  });

  const labelForRow = (row: string) => {
    switch (row) {
      case 'staffEngagementExercisesBehaviouralPsychologist':
        return 'Staff engagement exercises';
      case 'onlineTests':
        return 'Online tests';
      case 'grs':
        return 'Recruitment Services';
      case 'applicantTrackingSystems':
        return 'Applicant Tracking Systems';
      case 'preEmploymentChecks':
        return 'Pre-employment checks';
      case 'executiveSearchTeams':
        return 'Executive search teams';
      case 'complexCampaignManagementFees':
        return 'Complex campaign management fees';
      case 'advertisingServices':
        return 'Advertising Services';
      case 'other':
        return 'Other';
      default:
        return row;
    }
  };

  const headerLabel = (col: string) => {
    if (col === 'Yes') return 'Yes';
    if (col === 'No') return 'No';
    if (col === 'Partial') return 'Partial';
    if (col === 'NA') return 'N/A';
    return col;
  };

  const triggerChange = useCallback(
    (updated: RowMap, otherSpecifyValue: string) => {
      const flat: Record<string, string> = {};
      getRows().forEach((row) => {
        getColumns().forEach((col) => {
          flat[`${row}${col}`] = updated[row][col];
        });
      });
      const trimmedOtherSpecify = otherSpecifyValue.trim();
      if (trimmedOtherSpecify) {
        flat.otherSpecify = trimmedOtherSpecify;
      }
      const asString = objectToString(flat);
      onChange({ target: { name: question.name, value: dateIsEmpty(asString) ? '' : asString } });
    },
    [onChange, question.name],
  );

  const isOtherSelected = useMemo(
    () => getColumns().some((column) => values.other?.[column] === '1'),
    [values],
  );

  const setRowSelection = useCallback(
    (row: string, col: string, checked: boolean) => {
      setValues((prev) => {
        const nextRow = { ...prev[row] };
        getColumns().forEach((c) => {
          nextRow[c] = '0';
        });
        if (checked) nextRow[col] = '1';
        const next = { ...prev, [row]: nextRow };
        const nextOtherSelected = getColumns().some(
          (column) => next.other?.[column] === '1',
        );
        const nextOtherSpecify = nextOtherSelected ? otherSpecify : '';
        if (!nextOtherSelected && otherSpecify) {
          setOtherSpecify('');
        }
        triggerChange(next, nextOtherSpecify);
        return next;
      });
    },
    [otherSpecify, triggerChange],
  );

  const Row = memo(function Row({
    row,
    rowValues,
  }: {
    row: string;
    rowValues: Record<string, string>;
  }) {
    return (
      <tr className="govuk-table__row">
        <th scope="row" className="govuk-table__header">
          {labelForRow(row)}
        </th>
        {getColumns().map((col) => {
          const field = `${row}${col}`;
          const checked = rowValues[col] === '1';
          const label = `${labelForRow(row)} ${headerLabel(col)}`;
          return (
            <td key={field} className="govuk-table__cell">
              <div className="govuk-checkboxes">
                <div className="govuk-checkboxes__item">
                  <input
                    id={makeId(field)}
                    type="checkbox"
                    className="govuk-checkboxes__input"
                    disabled={disabled}
                    checked={checked}
                    onChange={(event) => setRowSelection(row, col, event.target.checked)}
                  />
                  <label className="govuk-label govuk-checkboxes__label" htmlFor={makeId(field)}>
                    <span className="govuk-visually-hidden">{label}</span>
                  </label>
                </div>
              </div>
            </td>
          );
        })}
      </tr>
    );
  });

  return (
    <>
      {error && <ErrorMessage error={error} />}
      {detailsHeading && <Details heading={detailsHeading} content={detailsContent} />}

      <table className="govuk-table core-cost-external-table">
        <style>{`
          .core-cost-external-table .govuk-table__head .govuk-table__header {
            position: sticky;
            top: 0;
            background: #ffffff;
            z-index: 2;
          }
          .core-cost-external .govuk-checkboxes,
          .core-cost-external .govuk-checkboxes__item {
            margin: 0;
          }
          .core-cost-external .govuk-table__cell,
          .core-cost-external .govuk-table__header {
            vertical-align: top;
          }
          .core-cost-external .govuk-table__cell {
            vertical-align: middle;
            padding-top: 6px;
            padding-bottom: 4px;
          }
          .core-cost-external .govuk-table__header {
            line-height: 1.05;
            padding-top: 6px;
            padding-bottom: 4px;
          }
        `}</style>
        <thead className="govuk-table__head">
          <tr className="govuk-table__row">
            <th scope="col" className="govuk-table__header">
              Cost
            </th>
            {getColumns().map((col) => (
              <th key={col} scope="col" className="govuk-table__header">
                {headerLabel(col)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="govuk-table__body core-cost-external">
          {getRows().map((row) => (
            <Row key={row} row={row} rowValues={values[row]} />
          ))}
        </tbody>
      </table>
      {isOtherSelected && (
        <div className="govuk-form-group govuk-!-margin-top-4">
          <label className="govuk-label" htmlFor={makeId('otherSpecify')}>
            Other - Please specify
          </label>
          <input
            id={makeId('otherSpecify')}
            className="govuk-input govuk-!-width-two-thirds"
            type="text"
            disabled={disabled}
            value={otherSpecify}
            onChange={(event) => {
              const nextValue = event.target.value;
              setOtherSpecify(nextValue);
              triggerChange(values, nextValue);
            }}
          />
        </div>
      )}
    </>
  );
}
